import React, { useEffect, useRef } from 'react';

const RainBackground = () => {
  const containerRef = useRef(null);
  const scriptLoadedRef = useRef(false);
  const canvasRef = useRef(null);
  const initAttemptsRef = useRef(0);
  const resizeTimeoutRef = useRef(null);

  useEffect(() => {
    if (scriptLoadedRef.current) return;
    
    // Scrollbar policy: the page's single scrollbar lives on <html> (see index.css).
    // Body must not grow its own scrollbar, otherwise there are two visible
    // scrollbars stacked side-by-side. We block any script attempt to give
    // body its own overflow:hidden/scroll and force overflow-y:visible instead.
    let scrollbarProtectionSetup = false;
    const protectScrollbarImmediate = () => {
      const body = document.body;
      if (!body || scrollbarProtectionSetup) return;

      const bodyStyle = body.style;

      try {
        const originalOverflowY = Object.getOwnPropertyDescriptor(bodyStyle, 'overflowY') ||
          Object.getOwnPropertyDescriptor(CSSStyleDeclaration.prototype, 'overflowY');

        if (originalOverflowY && originalOverflowY.set && !bodyStyle._overflowYIntercepted) {
          Object.defineProperty(bodyStyle, 'overflowY', {
            set: function (value) {
              // Never let anything set body overflow-y to scroll/hidden/auto —
              // the scrollbar is on <html>, body stays "visible".
              if (value === 'hidden' || value === 'scroll' || value === 'auto' || value === '') {
                originalOverflowY.set.call(this, 'visible');
              } else {
                originalOverflowY.set.call(this, value);
              }
            },
            get: function () {
              return originalOverflowY.get ? originalOverflowY.get.call(this) : 'visible';
            },
            configurable: true,
          });
          bodyStyle._overflowYIntercepted = true;
        }

        const originalOverflow = Object.getOwnPropertyDescriptor(bodyStyle, 'overflow') ||
          Object.getOwnPropertyDescriptor(CSSStyleDeclaration.prototype, 'overflow');

        if (originalOverflow && originalOverflow.set && !bodyStyle._overflowIntercepted) {
          Object.defineProperty(bodyStyle, 'overflow', {
            set: function (value) {
              // Strip any overflow shorthand. Keep body overflow-y visible so
              // the html scrollbar stays the only one on the page.
              setTimeout(() => {
                bodyStyle.setProperty('overflow-x', 'hidden', 'important');
                bodyStyle.setProperty('overflow-y', 'visible', 'important');
              }, 0);
            },
            get: function () {
              return 'hidden visible';
            },
            configurable: true,
          });
          bodyStyle._overflowIntercepted = true;
        }

        if (!bodyStyle._setPropertyIntercepted) {
          const originalSetProperty = bodyStyle.setProperty;
          bodyStyle.setProperty = function (property, value, priority) {
            if (property === 'overflow-y' || property === 'overflowY') {
              if (value === 'hidden' || value === 'scroll' || value === 'auto' || value === '') {
                return originalSetProperty.call(this, 'overflow-y', 'visible', 'important');
              }
            } else if (property === 'overflow') {
              originalSetProperty.call(this, 'overflow-x', 'hidden', priority || 'important');
              originalSetProperty.call(this, 'overflow-y', 'visible', 'important');
              return;
            }
            return originalSetProperty.call(this, property, value, priority);
          };
          bodyStyle._setPropertyIntercepted = true;
        }

        if (!bodyStyle._initialScrollbarSet) {
          bodyStyle.setProperty('overflow-x', 'hidden', 'important');
          bodyStyle.setProperty('overflow-y', 'visible', 'important');
          bodyStyle._initialScrollbarSet = true;
        }

        scrollbarProtectionSetup = true;
      } catch (e) {
        console.error('Error setting up scrollbar protection:', e);
      }
    };
    
    // Run immediately
    protectScrollbarImmediate();
    
    // Also run after a tiny delay to catch anything that runs synchronously
    setTimeout(protectScrollbarImmediate, 0);
    setTimeout(protectScrollbarImmediate, 10);
    
    // Create slide element FIRST - before script loads
    // The Codrops script looks for this during initialization
    // Use drizzle weather type for slower speed
    let rainSlide = document.querySelector('[data-weather="drizzle"]');
    if (!rainSlide) {
      rainSlide = document.createElement('div');
      rainSlide.setAttribute('data-weather', 'drizzle'); // Use drizzle instead of rain
      rainSlide.setAttribute('id', 'rain-slide');
      rainSlide.className = 'slide slide--current';
      rainSlide.style.display = 'none';
      document.body.appendChild(rainSlide);
    }
    
    // Create navigation link element - script expects this for classList operations
    let navLink = document.querySelector('[href="#rain-slide"]');
    if (!navLink) {
      navLink = document.createElement('a');
      navLink.setAttribute('href', '#rain-slide');
      navLink.className = 'nav-item--current';
      navLink.style.display = 'none';
      document.body.appendChild(navLink);
    }
    
    // Create canvas FIRST - the script looks for it immediately
    if (!containerRef.current) return;
    
    const canvas = document.createElement('canvas');
    canvas.id = 'container';
    const dpi = window.devicePixelRatio || 1;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 1920;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 1080;
    canvas.width = viewportWidth * dpi;
    canvas.height = viewportHeight * dpi;
    canvas.style.position = 'fixed';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100vh';
    canvas.style.minWidth = '100%';
    canvas.style.minHeight = '100vh';
    canvas.style.maxWidth = '100%';
    canvas.style.maxHeight = '100vh';
    canvas.style.zIndex = '0';
    canvas.style.pointerEvents = 'none';
    canvas.style.objectFit = 'cover';
    
    containerRef.current.appendChild(canvas);
    canvasRef.current = canvas;

    // Load the rain effect script
    const script = document.createElement('script');
    script.src = '/rain-effect.js?v=' + Date.now(); // Cache busting
    script.async = false; // Load synchronously so it can find the canvas
    
    script.onload = () => {
      scriptLoadedRef.current = true;
      console.log('Rain effect script loaded');
      
      // Set initial background image (will be shown if resize happens)
      document.body.style.backgroundImage = "url('/img/weather/texture-graves-bg.png')";
      document.body.style.backgroundSize = 'cover';
      document.body.style.backgroundPosition = 'center';
      document.body.style.backgroundRepeat = 'no-repeat';
      document.body.style.backgroundAttachment = 'fixed';
      
      // Re-apply scrollbar protection after script loads (in case it was reset)
      const protectScrollbar = () => {
        const body = document.body;
        
        if (!body) return;
        
        // Intercept overflowY setter on body
        const bodyStyle = body.style;
        const originalBodyOverflowY = Object.getOwnPropertyDescriptor(bodyStyle, 'overflowY') || 
          Object.getOwnPropertyDescriptor(CSSStyleDeclaration.prototype, 'overflowY');
        
        if (originalBodyOverflowY && originalBodyOverflowY.set) {
          Object.defineProperty(bodyStyle, 'overflowY', {
            set: function(value) {
              if (value === 'hidden' || value === 'scroll' || value === 'auto' || value === '') {
                originalBodyOverflowY.set.call(this, 'visible');
              } else {
                originalBodyOverflowY.set.call(this, value);
              }
            },
            get: function() {
              return originalBodyOverflowY.get ? originalBodyOverflowY.get.call(this) : 'visible';
            },
            configurable: true
          });
        }
        
        // Intercept overflow shorthand property (this is what's being set!)
        const originalBodyOverflow = Object.getOwnPropertyDescriptor(bodyStyle, 'overflow') || 
          Object.getOwnPropertyDescriptor(CSSStyleDeclaration.prototype, 'overflow');
        
        if (originalBodyOverflow && originalBodyOverflow.set) {
          Object.defineProperty(bodyStyle, 'overflow', {
            set: function(value) {
              // Log who's trying to change overflow shorthand
              const stack = new Error().stack;
              if (typeof value === 'string') {
                // If it contains 'hidden', replace with 'scroll'
                if (value.includes('hidden')) {
                  console.warn('Blocked attempt to set body overflow to:', value);
                  console.trace('Call stack:', stack);
                  // Keep body overflow-y visible so the scrollbar stays on <html>.
                  originalBodyOverflow.set.call(this, 'hidden visible');
                  bodyStyle.setProperty('overflow-y', 'visible', 'important');
                } else if (value === 'auto' || value === '') {
                  console.log('Converting overflow auto to hidden visible');
                  originalBodyOverflow.set.call(this, 'hidden visible');
                  bodyStyle.setProperty('overflow-y', 'visible', 'important');
                } else {
                  originalBodyOverflow.set.call(this, value);
                  bodyStyle.setProperty('overflow-y', 'visible', 'important');
                }
              } else {
                originalBodyOverflow.set.call(this, value);
              }
            },
            get: function() {
              return originalBodyOverflow.get ? originalBodyOverflow.get.call(this) : 'hidden visible';
            },
            configurable: true
          });
        }
        
        // Also intercept setProperty method
        const originalBodySetProperty = bodyStyle.setProperty;
        bodyStyle.setProperty = function(property, value, priority) {
          if (property === 'overflow-y' || property === 'overflowY') {
            if (value === 'hidden' || value === 'scroll' || value === 'auto' || value === '') {
              value = 'visible';
              priority = 'important';
            }
          } else if (property === 'overflow') {
            originalBodySetProperty.call(this, 'overflow-x', 'hidden', priority || 'important');
            originalBodySetProperty.call(this, 'overflow-y', 'visible', 'important');
            return;
          }
          return originalBodySetProperty.call(this, property, value, priority);
        };

        // Scrollbar lives on <html>; keep body visible.
        body.style.setProperty('overflow-x', 'hidden', 'important');
        body.style.setProperty('overflow-y', 'visible', 'important');
      };
      
      // Run immediately
      protectScrollbar();
      
      // Run after delays to catch any script modifications
      setTimeout(protectScrollbar, 50);
      setTimeout(protectScrollbar, 100);
      setTimeout(protectScrollbar, 200);
      setTimeout(protectScrollbar, 500);
      setTimeout(protectScrollbar, 1000);
      
      // Continuously check and restore scrollbar - but less frequently to avoid flashing
      // The interception should handle most cases, this is just a backup
      let lastCheck = 0;
      const scrollbarCheckInterval = setInterval(() => {
        const now = Date.now();
        // Only check every 200ms to avoid flashing
        if (now - lastCheck < 200) return;
        lastCheck = now;
        
        const body = document.body;
        
        if (body) {
          const overflowY = body.style.overflowY;
          const overflow = body.style.overflow;
          
          // Only fix if actually wrong, and do it silently.
          if (overflow && typeof overflow === 'string' && overflow.includes('hidden')) {
            body.style.setProperty('overflow-x', 'hidden', 'important');
            body.style.setProperty('overflow-y', 'visible', 'important');
          } else if (overflowY === 'hidden' || overflowY === 'scroll' || overflowY === '') {
            body.style.setProperty('overflow-y', 'visible', 'important');
          }
        }
      }, 200); // Check every 200ms - less aggressive to avoid flashing
      
      // Store interval for cleanup
      window._scrollbarCheckInterval = scrollbarCheckInterval;
      
      // Monitor for style changes and restore scrollbar
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
            const target = mutation.target;
            if (target === document.body) {
              // Immediately fix any inline style changes
              const body = document.body;
              const currentOverflow = body.style.overflow;
              const currentOverflowY = body.style.overflowY;
              
              if (currentOverflow && typeof currentOverflow === 'string') {
                body.style.removeProperty('overflow');
                body.style.setProperty('overflow-x', 'hidden', 'important');
                body.style.setProperty('overflow-y', 'visible', 'important');
              }
              if (currentOverflowY === 'hidden' || currentOverflowY === 'scroll' || currentOverflowY === '') {
                body.style.setProperty('overflow-y', 'visible', 'important');
              }
            }
          }
        });
      });
      
      observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });
      
      // Store observer for cleanup
      window._scrollbarObserver = observer;
      
      // Add cache busting to texture images by intercepting Image loading
      const originalImage = window.Image;
      window.Image = function() {
        const img = new originalImage();
        const originalSrcSetter = Object.getOwnPropertyDescriptor(HTMLImageElement.prototype, 'src').set;
        Object.defineProperty(img, 'src', {
          set: function(value) {
            // Add cache busting to texture images
            if (value && typeof value === 'string' && value.includes('texture-graves-bg.png')) {
              const separator = value.includes('?') ? '&' : '?';
              value = value + separator + 'v=' + Date.now();
            }
            originalSrcSetter.call(this, value);
          },
          get: function() {
            return this.getAttribute('src');
          }
        });
        return img;
      };
      
      // Override rain settings aggressively - directly modify raindrops object
      const setDrizzleSpeed = () => {
        let applied = false;
        
        // Method 1: Modify weatherData before it's used
        if (window.Z && window.Z.rain) {
          window.Z.rain.rainChance = 0.05; // Very slow
          window.Z.rain.dropletsRate = 3; // Very few droplets
          window.Z.rain.rainLimit = 1;
          window.Z.rain.minR = 10;
          window.Z.rain.maxR = 40;
          window.Z.rain.dropletsSize = [3.5, 6];
          window.Z.rain.trailRate = 0.5;
          applied = true;
          console.log('Modified weatherData.rain:', window.Z.rain);
        }
        
        // Method 2: Directly modify raindrops options
        if (window.raindrops) {
          if (!window.raindrops.options) {
            window.raindrops.options = {};
          }
          window.raindrops.options.rainChance = 0.05;
          window.raindrops.options.dropletsRate = 3;
          window.raindrops.options.rainLimit = 1;
          window.raindrops.options.minR = 10;
          window.raindrops.options.maxR = 40;
          window.raindrops.options.dropletsSize = [3.5, 6];
          window.raindrops.options.trailRate = 0.5;
          window.raindrops.options.globalTimeScale = 0.3; // Very slow
          
          // Also try to modify internal properties directly
          if (window.raindrops.rainChance !== undefined) {
            window.raindrops.rainChance = 0.05;
          }
          if (window.raindrops.dropletsRate !== undefined) {
            window.raindrops.dropletsRate = 3;
          }
          if (window.raindrops.rainLimit !== undefined) {
            window.raindrops.rainLimit = 1;
          }
          if (window.raindrops.globalTimeScale !== undefined) {
            window.raindrops.globalTimeScale = 0.3;
          }
          
          console.log('Modified raindrops object:', {
            options: window.raindrops.options,
            rainChance: window.raindrops.rainChance,
            dropletsRate: window.raindrops.dropletsRate
          });
          applied = true;
        }
        
        // Method 3: Override updateWeather to use drizzle settings
        if (window.updateWeather && typeof window.updateWeather === 'function') {
          const originalUpdateWeather = window.updateWeather;
          window.updateWeather = function() {
            const result = originalUpdateWeather.apply(this, arguments);
            // Immediately override after updateWeather runs
            setTimeout(() => {
              if (window.Z && window.Z.rain) {
                window.Z.rain.rainChance = 0.05;
                window.Z.rain.dropletsRate = 3;
                window.Z.rain.rainLimit = 1;
              }
              if (window.raindrops && window.raindrops.options) {
                window.raindrops.options.rainChance = 0.05;
                window.raindrops.options.dropletsRate = 3;
                window.raindrops.options.rainLimit = 1;
                window.raindrops.options.globalTimeScale = 0.3;
              }
            }, 10);
            return result;
          };
          console.log('Wrapped updateWeather function');
          applied = true;
        }
        
        return applied;
      };
      
      // Wait for script to initialize, then manually set up rain weather
      const tryInitRain = () => {
        initAttemptsRef.current++;
        
        // Check if the script has initialized (look for raindrops object or weatherData)
        // The script should have created these after images load
        if (window.raindrops || (window.Z && window.K)) {
          // Set drizzle speed first
          setDrizzleSpeed();
          
          // Try to trigger updateWeather by simulating hash change
          // Or directly set the weather if we can access the variables
          try {
            // Set hash to trigger updateWeather
            const originalHash = window.location.hash;
            window.location.hash = '#rain-slide';
            
            // Wait a moment for hash change to process
            setTimeout(() => {
              if (originalHash !== window.location.hash) {
                window.location.hash = originalHash || '';
              }
              // Set drizzle speed again after updateWeather runs
              setTimeout(() => {
                setDrizzleSpeed();
                
                // Continuously reapply settings aggressively
                const keepSlow = setInterval(() => {
                  if (window.Z && window.Z.rain) {
                    window.Z.rain.rainChance = 0.05;
                    window.Z.rain.dropletsRate = 3;
                    window.Z.rain.rainLimit = 1;
                  }
                  if (window.raindrops) {
                    if (window.raindrops.options) {
                      window.raindrops.options.rainChance = 0.05;
                      window.raindrops.options.dropletsRate = 3;
                      window.raindrops.options.rainLimit = 1;
                      window.raindrops.options.globalTimeScale = 0.3;
                    }
                    // Try direct properties too
                    if (window.raindrops.rainChance !== undefined) {
                      window.raindrops.rainChance = 0.05;
                    }
                    if (window.raindrops.dropletsRate !== undefined) {
                      window.raindrops.dropletsRate = 3;
                    }
                    if (window.raindrops.globalTimeScale !== undefined) {
                      window.raindrops.globalTimeScale = 0.3;
                    }
                  }
                }, 500); // Check every 500ms
                
                // Keep it running indefinitely
                window._rainSlowInterval = keepSlow;
              }, 200);
              console.log('Rain weather initialized with drizzle speed');
            }, 100);
          } catch (e) {
            console.error('Error initializing rain:', e);
            // Fallback: try to access internal variables directly
            if (window.Z && window.Z.rain && window.updateWeather) {
              try {
                setDrizzleSpeed();
                window.updateWeather();
                setTimeout(() => setDrizzleSpeed(), 200);
              } catch (e2) {
                console.error('Fallback init failed:', e2);
              }
            }
          }
        } else if (initAttemptsRef.current < 100) {
          // Retry after a short delay - script may still be loading images
          setTimeout(tryInitRain, 200);
        } else {
          console.warn('Could not initialize rain weather after multiple attempts');
        }
      };
      
      // Start trying to initialize after images have time to load
      setTimeout(tryInitRain, 1000);
    };

    script.onerror = () => {
      console.error('Failed to load rain effect script');
    };

    document.body.appendChild(script);

    // Track previous window dimensions to detect actual resize vs scroll/rubber band
    let lastWidth = window.innerWidth;
    let lastHeight = window.innerHeight;
    const RESIZE_THRESHOLD = 50; // Only disable if width/height changes by more than 50px
    
    // Handle window resize - Remove raindrops and show static background
    // Only disable on actual size changes, not scroll/rubber band effects
    const handleResize = () => {
      const currentWidth = window.innerWidth;
      const currentHeight = window.innerHeight;
      
      // Calculate actual change
      const widthChange = Math.abs(currentWidth - lastWidth);
      const heightChange = Math.abs(currentHeight - lastHeight);
      
      // On mobile, address bar show/hide causes small height changes
      // Only disable if there's a significant size change (likely orientation change or window resize)
      const isSignificantChange = widthChange > RESIZE_THRESHOLD || heightChange > RESIZE_THRESHOLD;
      
      // Update last dimensions
      lastWidth = currentWidth;
      lastHeight = currentHeight;
      
      // Only proceed if it's a significant change
      if (!isSignificantChange) {
        return; // Ignore small changes (likely scroll/rubber band)
      }
      
      // Debounce resize to avoid too many rapid operations
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        // Double-check it's still a significant change
        const finalWidth = window.innerWidth;
        const finalHeight = window.innerHeight;
        const finalWidthChange = Math.abs(finalWidth - lastWidth);
        const finalHeightChange = Math.abs(finalHeight - lastHeight);
        
        if (finalWidthChange > RESIZE_THRESHOLD || finalHeightChange > RESIZE_THRESHOLD) {
          // Hide/remove the canvas and raindrops
          if (canvasRef.current) {
            canvasRef.current.style.display = 'none';
          }
          
          // Stop any rain animations/intervals
          if (window._rainSlowInterval) {
            clearInterval(window._rainSlowInterval);
            window._rainSlowInterval = null;
          }
          
          // Set the background image on the body
          document.body.style.backgroundImage = "url('/img/weather/texture-graves-bg.png')";
          document.body.style.backgroundSize = 'cover';
          document.body.style.backgroundPosition = 'center';
          document.body.style.backgroundRepeat = 'no-repeat';
          document.body.style.backgroundAttachment = 'fixed';
        }
      }, 300); // Wait 300ms after resize stops
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      // Cleanup
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (script && script.parentNode) {
        script.parentNode.removeChild(script);
      }
      if (canvasRef.current && canvasRef.current.parentNode) {
        canvasRef.current.parentNode.removeChild(canvasRef.current);
      }
      const slide = document.querySelector('#rain-slide');
      if (slide && slide.parentNode) {
        slide.parentNode.removeChild(slide);
      }
      const nav = document.querySelector('[href="#rain-slide"]');
      if (nav && nav.parentNode) {
        nav.parentNode.removeChild(nav);
      }
      if (window._rainSlowInterval) {
        clearInterval(window._rainSlowInterval);
      }
      if (window._scrollbarObserver) {
        window._scrollbarObserver.disconnect();
      }
      if (window._scrollbarCheckInterval) {
        clearInterval(window._scrollbarCheckInterval);
      }
      window.removeEventListener('resize', handleResize);
      
      // Leave body overflow-y visible on cleanup so the html scrollbar remains the only one.
      if (document.body) {
        document.body.style.setProperty('overflow-y', 'visible', 'important');
      }
    };
  }, []);

  return (
    <div 
      ref={containerRef} 
      className="rain-background"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100vh',
        minWidth: '100%',
        minHeight: '100vh',
        maxWidth: '100%',
        maxHeight: '100vh',
        zIndex: 0,
        pointerEvents: 'none',
        overflow: 'hidden'
      }}
    />
  );
};

export default RainBackground;


